// ─────────────────────────────────────────────
//  Sandbox local store — collection maps plus debounced IndexedDB snapshots.
//
//  Data only: no Firestore shapes, no query semantics, no React. localDb.js
//  puts the Firestore face on top of this.
//
//  Persistence NEVER throws. This module is constructed from config.js at
//  module scope, and a throw there kills the module graph before React mounts.
//  Losing persistence costs a demo's ORs; throwing costs the whole app.
// ─────────────────────────────────────────────
const DB_NAME = "atc-sandbox";
const STORE_NAME = "snapshots";
const PERSIST_DEBOUNCE_MS = 250;

const clone = (v) =>
  v === undefined ? undefined
    : typeof structuredClone === "function" ? structuredClone(v)
    : JSON.parse(JSON.stringify(v));

const collectionOf = (path) => path.split("/")[0];
const idOf = (path) => path.split("/").slice(1).join("/");

/**
 * Delete ONE persisted snapshot.
 *
 * ⚠️ A SNAPSHOT IS A KEY, NOT A DATABASE. Every store shares the single
 * `atc-sandbox` database and distinguishes itself by `persistKey` inside the
 * `snapshots` object store. Calling `indexedDB.deleteDatabase(persistKey)`
 * therefore deletes a database that has never existed — and IndexedDB reports
 * that as SUCCESS, so a caller "cleaning up" is told it worked while the data
 * sits there permanently. Testing mode captures a real facility's day, so that
 * mistake means real patient-adjacent data persisting on disk indefinitely and
 * bleeding into the next session, which hydrates before it re-captures.
 *
 * Never throws: callers use this on the way out and must reload regardless.
 */
export function deleteStoredSnapshot(
  persistKey,
  indexedDB = typeof globalThis !== "undefined" ? globalThis.indexedDB : null
) {
  return new Promise((resolve) => {
    if (!indexedDB || !persistKey) return resolve(false);
    let req;
    try { req = indexedDB.open(DB_NAME, 1); } catch { return resolve(false); }
    req.onerror = () => resolve(false);
    req.onupgradeneeded = () => {
      // The database did not exist, so neither did the snapshot. Nothing to do.
      try { req.result.createObjectStore(STORE_NAME); } catch { /* ignore */ }
    };
    req.onsuccess = () => {
      const idb = req.result;
      try {
        const tx = idb.transaction(STORE_NAME, "readwrite");
        tx.oncomplete = () => resolve(true);
        tx.onerror = () => resolve(false);
        tx.onabort = () => resolve(false);
        tx.objectStore(STORE_NAME).delete(persistKey);
      } catch {
        resolve(false);
      }
    };
    return undefined;
  });
}

export function createStore({
  persistKey,
  persist = true,
  indexedDB = typeof globalThis !== "undefined" ? globalThis.indexedDB : null,
} = {}) {
  // collection name -> Map<docId, data>
  const collections = new Map();
  // collection name -> Set<listener>
  const listeners = new Map();
  let timer = null;

  // While suspended, bump() records touched collections instead of notifying.
  // resume() then fires exactly ONE notification per touched collection, so a
  // batch/transaction of several writes does not make listeners see every
  // half-written intermediate state.
  let suspended = false;
  const touchedWhileSuspended = new Set();

  const colMap = (name) => {
    if (!collections.has(name)) collections.set(name, new Map());
    return collections.get(name);
  };

  const openIdb = () =>
    new Promise((resolve) => {
      if (!persist || !indexedDB) return resolve(null);
      let req;
      try { req = indexedDB.open(DB_NAME, 1); } catch { return resolve(null); }
      req.onupgradeneeded = () => {
        try { req.result.createObjectStore(STORE_NAME); } catch { /* exists */ }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
      req.onblocked = () => resolve(null);
    });

  const idbPromise = openIdb();

  const serialize = () => {
    const out = {};
    for (const [name, map] of collections) out[name] = Object.fromEntries(map);
    return out;
  };

  const hydrate = (raw) => {
    if (!raw || typeof raw !== "object") return;
    for (const [name, docs] of Object.entries(raw)) {
      const map = colMap(name);
      for (const [id, data] of Object.entries(docs || {})) map.set(id, data);
    }
  };

  const ready = (async () => {
    const idb = await idbPromise;
    if (!idb) return;
    await new Promise((resolve) => {
      try {
        const tx = idb.transaction(STORE_NAME, "readonly");
        const req = tx.objectStore(STORE_NAME).get(persistKey);
        req.onsuccess = () => { hydrate(req.result); resolve(); };
        req.onerror = () => resolve();
      } catch { resolve(); }
    });
  })();

  // Resolves when the write has actually COMMITTED, not merely been queued.
  // Callers that reload immediately afterwards depend on that difference; a
  // fire-and-forget put would let the page navigate mid-transaction.
  const flush = async () => {
    const idb = await idbPromise;
    if (!idb) return;
    try {
      await new Promise((resolve) => {
        const tx = idb.transaction(STORE_NAME, "readwrite");
        // Resolve on any outcome — a lost snapshot is survivable, a hang is not.
        tx.oncomplete = () => resolve();
        tx.onerror = () => resolve();
        tx.onabort = () => resolve();
        tx.objectStore(STORE_NAME).put(serialize(), persistKey);
      });
    } catch { /* a lost snapshot is survivable; a throw is not */ }
  };

  const schedulePersist = () => {
    if (!persist) return;
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => { timer = null; flush(); }, PERSIST_DEBOUNCE_MS);
  };

  const notify = (name) => {
    const set = listeners.get(name);
    if (!set) return;
    for (const fn of Array.from(set)) {
      try { fn(); } catch (err) { console.error("[sandbox] listener threw", err); }
    }
  };

  const bump = (name) => {
    // Persistence is scheduled regardless of suspension — a demo's writes
    // must survive a reload even if they landed inside a suspended group.
    schedulePersist();
    if (suspended) {
      touchedWhileSuspended.add(name);
      return;
    }
    notify(name);
  };

  return {
    ready,

    getDoc(path) {
      return clone(colMap(collectionOf(path)).get(idOf(path)));
    },

    setDoc(path, data) {
      colMap(collectionOf(path)).set(idOf(path), clone(data));
      bump(collectionOf(path));
    },

    mergeDoc(path, patch) {
      const map = colMap(collectionOf(path));
      const id = idOf(path);
      map.set(id, { ...(map.get(id) || {}), ...clone(patch) });
      bump(collectionOf(path));
    },

    deleteDoc(path) {
      colMap(collectionOf(path)).delete(idOf(path));
      bump(collectionOf(path));
    },

    listDocs(name) {
      return Array.from(colMap(name).entries())
        .map(([id, data]) => ({ id, path: `${name}/${id}`, data: clone(data) }));
    },

    subscribe(name, fn) {
      if (!listeners.has(name)) listeners.set(name, new Set());
      listeners.get(name).add(fn);
      return () => listeners.get(name)?.delete(fn);
    },

    // Used by writeBatch/runTransaction to emit ONE notification per collection
    // after a group of writes rather than one per write.
    _bump: bump,

    // Force the debounced snapshot to disk NOW and wait for it.
    //
    // ⚠️ `ready` IS NOT THIS. `ready` resolves when HYDRATION finishes — it says
    // nothing about whether later writes have been persisted. A caller that
    // fills the store and immediately reloads (testing mode does exactly that)
    // races the 250ms debounce and boots into a half-written store, which looks
    // exactly like a facility that had a quiet day.
    async flushNow() {
      if (timer) { clearTimeout(timer); timer = null; }
      await flush();
    },

    // Suspend/resume bracket a group of writes (batches, transactions) so
    // subscribers see one notification per touched collection at the end,
    // never the half-written state in between.
    suspend() { suspended = true; },

    resume() {
      suspended = false;
      const touched = Array.from(touchedWhileSuspended);
      touchedWhileSuspended.clear();
      for (const name of touched) notify(name);
    },
  };
}
